/**
 * Board Context Menu Component
 * Right-click menu on the board background (not on tasks)
 */

import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, CheckSquare, ClipboardList } from 'lucide-react';

interface BoardContextMenuProps {
  x: number;
  y: number;
  onAddTask: () => void;
  onSelectAllInColumn: () => void;
  onOpenTaskPool: () => void;
  onClose: () => void;
}

export function BoardContextMenu({ x, y, onAddTask, onSelectAllInColumn, onOpenTaskPool, onClose }: BoardContextMenuProps) {
  const { t } = useTranslation();

  useEffect(() => {
    const handleClick = () => onClose();
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    // Delay adding listener to avoid immediate close from the same click
    const timer = setTimeout(() => {
      window.addEventListener('click', handleClick);
      window.addEventListener('keydown', handleEscape);
    }, 0);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('click', handleClick);
      window.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  const buttonStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    width: '100%',
    padding: '8px 12px',
    fontSize: 10,
    color: '#1f2937',
    backgroundColor: 'transparent',
    border: 'none',
    borderRadius: 6,
    cursor: 'pointer',
    textAlign: 'left' as const,
  };

  return (
    <div
      style={{
        position: 'fixed',
        left: x,
        top: y,
        zIndex: 1000,
        backgroundColor: '#fff',
        border: '1px solid #e5e7eb',
        borderRadius: 8,
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        padding: 4,
        minWidth: 180,
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <button
        onClick={onAddTask}
        style={buttonStyle}
        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f3f4f6')}
        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
      >
        <Plus size={14} style={{ color: '#10b981' }} />
        {t('boardv3.contextMenu.addNewTask')}
      </button>
      <div style={{ borderTop: '1px solid #e5e7eb', margin: '2px 4px' }} />
      <button
        onClick={onSelectAllInColumn}
        style={buttonStyle}
        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f3f4f6')}
        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
      >
        <CheckSquare size={14} style={{ color: '#3b82f6' }} />
        {t('boardv3.contextMenu.selectAll')}
      </button>
      <button
        onClick={onOpenTaskPool}
        style={buttonStyle}
        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f3f4f6')}
        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
      >
        <ClipboardList size={14} style={{ color: '#8b5cf6' }} />
        {t('boardv3.contextMenu.openTaskPool')}
      </button>
    </div>
  );
}
